import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Boxes, Wallet, Hammer, ClipboardCheck, Palette, CheckCircle2, FileCheck2,
  AlertCircle, Search, X,
} from "lucide-react";
import ExecutionUnits from "../BV_user/ExecutionUnits";
import { getInteriorExecutionStats, getPreExecutionInteractions, getInteriorExecutionFilters } from "../../services/zumaleAdminInterior";
import PageHeader from "../../components/PageHeader";
import Tabs from "../../components/Tabs";
import StatTile from "../../components/StatTile";
import DataTable from "../../components/DataTable";
import PaginationControls from "../../components/PaginationControls";

const ADMIN_BASE = "/zumale-admin/interior-executions";

const fmtINR = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)} K`;
  return `₹${Math.round(v)}`;
};
const fmtInt = (n) => Number(n || 0).toLocaleString("en-IN");
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
export const humanizeStatus = (s) =>
  !s ? "—" : String(s).toLowerCase().split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

const pick = (o, c, s) => o?.[c] ?? o?.[s] ?? 0;

const STATUS_OPTIONS = [
  "ID_ACKNOWLEDGEMENT_PENDING", "ID_DESIGN_REQUEST_ACKNOWLEDGED", "ID_DESIGN_SUBMITTED",
  "BUYER_DESIGN_APPROVED", "BUYER_DESIGN_FEEDBACK_PROVIDED", "BUYER_DESIGN_REJECTED",
  "ID_DESIGN_FEEDBACK_ACKNOWLEDGED", "DEVELOPER_PENDING", "DEVELOPER_APPROVED",
  "DEVELOPER_REJECTED", "ID_ACKNOWLEDGEMENT_EXPIRED", "ID_DESIGN_DELIVERY_EXPIRED", "LOST",
];

const STATUS_PILL = (s) => {
  const u = String(s || "").toUpperCase();
  if (u.includes("APPROVED")) return "bg-emerald-50 text-emerald-700";
  if (u.includes("REJECTED") || u === "LOST" || u.includes("EXPIRED")) return "bg-rose-50 text-rose-600";
  if (u.includes("PENDING")) return "bg-amber-50 text-amber-700";
  return "bg-slate-100 text-slate-600";
};

/* ---------- Design Interactions (read-only) ---------- */
const InteractionsTab = ({ filters = {} }) => {
  const navigate = useNavigate();
  const [resp, setResp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const filterKey = JSON.stringify(filters);

  useEffect(() => { setPage(1); }, [status, filterKey]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    getPreExecutionInteractions({ ...filters, status: status || undefined, page, pageSize: 25 })
      .then((res) => mounted && setResp(res))
      .catch((e) => mounted && setError(e?.response?.data?.detail || "Failed to load interactions"))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [status, page, filterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = resp?.items || [];
  const totalPages = resp?.totalPages || 1;

  const columns = [
    { key: "designer", header: "Designer", width: 180, render: (r) => <span className="font-medium text-slate-800">{r.designerName || "—"}</span> },
    { key: "buyer", header: "Buyer", width: 160, render: (r) => r.buyerName || null },
    {
      key: "unit",
      header: "Unit · Project",
      width: 240,
      render: (r) => (
        <span className="text-slate-600">
          {[r.unitNo, r.projectName].filter(Boolean).join(" · ") || "—"}
          {r.phaseName ? <span className="text-slate-400"> · {r.phaseName}</span> : null}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: 200,
      render: (r) => (
        <>
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_PILL(r.latestStatus)}`}>{humanizeStatus(r.latestStatus)}</span>
          {r.developerApproved && <span className="ml-1.5 text-[10px] text-emerald-600 font-semibold">✓ Dev-approved</span>}
        </>
      ),
    },
    { key: "iterations", header: "Iterations", width: 90, align: "right", render: (r) => <span className="tabular-nums text-slate-700">{r.iterationCount}</span> },
    { key: "updated", header: "Updated", width: 130, render: (r) => <span className="text-xs text-slate-500">{fmtDate(r.latestStatusAt || r.createdAt)}</span> },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          className="rounded-lg border border-slate-300 text-xs py-1.5 px-2 bg-white focus:border-flamingo-500 focus:outline-none">
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{humanizeStatus(s)}</option>)}
        </select>
        <span className="text-xs text-slate-400">Read-only · pre-execution interactions</span>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl px-4 py-3 text-sm flex items-center gap-2"><AlertCircle size={16} /> {error}</div>
      )}

      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        minWidth={1000}
        rowKey="idLeadId"
        onRowClick={(r) => navigate(`${ADMIN_BASE}/interactions/${r.idLeadId}`, { state: { row: r } })}
        emptyState={<div className="px-6 py-10 text-center text-sm text-slate-400">No pre-execution interactions.</div>}
      />

      {totalPages > 1 && (
        <PaginationControls
          pagination={{ page: resp?.page ?? 1, pageSize: 25, total: resp?.total ?? 0, totalPages, hasNext: !!resp?.hasNext, hasPrevious: !!resp?.hasPrevious }}
          onPageChange={setPage}
          itemName="interactions"
        />
      )}
    </div>
  );
};

/* ---------- shared filter bar (developer → project → phase → config + search) ---------- */
const FilterBar = ({ opts, filters, setFilters, searchInput, setSearchInput }) => {
  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const projects = (opts.projects || []).filter((p) => !filters.developerId || String(p.parentId) === String(filters.developerId));
  const phases = (opts.phases || []).filter((p) => !filters.projectId || String(p.parentId) === String(filters.projectId));
  const configs = (opts.configs || []).filter((c) => !filters.projectPhaseId || String(c.parentId) === String(filters.projectPhaseId));
  const hasAny = filters.developerId || filters.projectId || filters.projectPhaseId || filters.propertyConfigId || searchInput;
  const selCls = "rounded-xl border border-slate-300 py-2 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-flamingo-500/40";
  return (
    <div className="flex flex-wrap items-center gap-2.5">
      <div className="relative flex-1 min-w-[220px]">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search buyer, unit, project, phase, designer, developer…"
          className="w-full rounded-xl border border-slate-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-500/40" />
      </div>
      <select className={selCls} value={filters.developerId}
        onChange={(e) => set({ developerId: e.target.value, projectId: "", projectPhaseId: "", propertyConfigId: "" })}>
        <option value="">All developers</option>
        {(opts.developers || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
      </select>
      <select className={selCls} value={filters.projectId}
        onChange={(e) => set({ projectId: e.target.value, projectPhaseId: "", propertyConfigId: "" })}>
        <option value="">All projects</option>
        {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <select className={selCls} value={filters.projectPhaseId}
        onChange={(e) => set({ projectPhaseId: e.target.value, propertyConfigId: "" })}>
        <option value="">All phases</option>
        {phases.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <select className={selCls} value={filters.propertyConfigId}
        onChange={(e) => set({ propertyConfigId: e.target.value })}>
        <option value="">All configs</option>
        {configs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      {hasAny && (
        <button onClick={() => { setSearchInput(""); setFilters({ developerId: "", projectId: "", projectPhaseId: "", propertyConfigId: "" }); }}
          className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-700">
          <X size={13} /> Clear
        </button>
      )}
    </div>
  );
};

const EMPTY_FILTERS = { developerId: "", projectId: "", projectPhaseId: "", propertyConfigId: "" };

/* ---------- page ---------- */
const InteriorExecutions = () => {
  const [tab, setTab] = useState("executions");
  const [stats, setStats] = useState(null);
  const [opts, setOpts] = useState({ developers: [], projects: [], phases: [], configs: [] });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let mounted = true;
    getInteriorExecutionStats().then((res) => mounted && setStats(res)).catch(() => {});
    getInteriorExecutionFilters().then((res) => mounted && res && setOpts(res)).catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Normalised filters passed to both tabs (ids → numbers, "" → undefined).
  const appliedFilters = useMemo(() => ({
    search: search || undefined,
    developerId: filters.developerId ? Number(filters.developerId) : undefined,
    projectId: filters.projectId ? Number(filters.projectId) : undefined,
    projectPhaseId: filters.projectPhaseId ? Number(filters.projectPhaseId) : undefined,
    propertyConfigId: filters.propertyConfigId ? Number(filters.propertyConfigId) : undefined,
  }), [search, filters]);

  const cards = useMemo(() => ([
    { icon: Palette, accent: "indigo", label: "Total leads", value: fmtInt(pick(stats, "totalLeads", "total_leads")) },
    { icon: ClipboardCheck, accent: "slate", label: "Developer-approved", value: fmtInt(pick(stats, "developerApproved", "developer_approved")) },
    { icon: Hammer, accent: "flamingo", label: "Executing", value: fmtInt(pick(stats, "executingPlans", "executing_plans")) },
    { icon: FileCheck2, accent: "emerald", label: "BOQ shared", value: fmtInt(pick(stats, "boqApproved", "boq_approved")) },
    { icon: CheckCircle2, accent: "emerald", label: "Completed plans", value: fmtInt(pick(stats, "completedPlans", "completed_plans")) },
    { icon: Boxes, accent: "slate", label: "Pre-execution", value: fmtInt(pick(stats, "preExecution", "pre_execution")) },
    { icon: Wallet, accent: "flamingo", label: "Execution revenue", value: fmtINR(pick(stats, "executionRevenue", "execution_revenue")) },
  ]), [stats]);

  const TABS = [
    { key: "executions", label: "Executions" },
    { key: "interactions", label: "Design Interactions" },
  ];

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)]">
      <PageHeader
        icon={Hammer}
        title="Interior Executions"
        subtitle="All ID executions & design interactions across developers"
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        {cards.map((c) => <StatTile key={c.label} dense {...c} />)}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <Tabs tabs={TABS} active={tab} onChange={setTab} />
        <div className="p-4 lg:p-5 flex flex-col gap-4">
          <FilterBar opts={opts} filters={filters} setFilters={setFilters}
            searchInput={searchInput} setSearchInput={setSearchInput} />

          {tab === "executions"
            ? <ExecutionUnits basePath={ADMIN_BASE} embedded controlledFilters={appliedFilters} />
            : <InteractionsTab filters={appliedFilters} />}
        </div>
      </div>
    </div>
  );
};

export default InteriorExecutions;
